/**
 * Production Ticker Intelligence Workspace.
 * Sibling of Strategy Tools (/ticker/:symbol). Read-only cached GETs only.
 */
import { useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TickerWorkspace } from "@/workspace/TickerWorkspace";
import {
  CachedBadge, WorkspaceAdminProvider, WorkspacePanel,
} from "@/workspace/primitives";
import { useWorkspaceLive } from "@/workspace/live/useWorkspaceLive";
import { WorkspaceLiveProvenance, WorkspaceLiveRequestAudit } from "@/workspace/live/WorkspaceLiveAdmin";
import {
  buildHandoffSearch,
  formatResearchHandoffCopy,
  parseFamily,
  parseHandoffSearch,
  resolveHandoff,
  strategyToolsPath,
  toIntelligenceTab,
  toWorkspaceTab,
} from "@/workspace/handoff";

export default function TickerIntelligencePage() {
  const { symbol: rawSymbol } = useParams();
  const symbol = String(rawSymbol || "").toUpperCase();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const parsed = useMemo(() => parseHandoffSearch(params), [params]);
  const workspaceTab = toWorkspaceTab(parsed.tab);
  const live = useWorkspaceLive(symbol, workspaceTab, { strategyLookup: parsed.strategy });
  const asOfDate = live.ticker?.asOf?.ohlcv
    || live.ticker?.series?.[live.ticker.series.length - 1]?.date
    || null;
  const resolved = useMemo(
    () => resolveHandoff(parsed, {
      asOfDate,
      expirations: live.ticker?.availableExpirations,
    }),
    [parsed, asOfDate, live.ticker],
  );
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!resolved.expiry || resolved.dte == null) return;
    if (parsed.dte === resolved.dte) return;
    const next = buildHandoffSearch({
      expiry: resolved.expiry,
      dte: resolved.dte,
      family: resolved.family,
      strategy: resolved.strategy,
      tab: parsed.tab,
    }, { includeTab: true, asOfDate, expirations: live.ticker?.availableExpirations });
    setParams(next, { replace: true });
  }, [parsed.dte, parsed.tab, resolved.expiry, resolved.dte, resolved.family, resolved.strategy, asOfDate, live.ticker, setParams]);

  useEffect(() => {
    const focus = live.ticker?.researchFocus;
    if (!focus || (focus.kind !== "ready" && focus.kind !== "limited")) return;
    const family = parseFamily(focus.family);
    if (!family || parsed.family === family) return;
    const next = buildHandoffSearch({
      expiry: resolved.expiry,
      dte: resolved.dte,
      family,
      strategy: resolved.strategy,
      tab: parsed.tab,
    }, { includeTab: true, asOfDate, expirations: live.ticker?.availableExpirations });
    setParams(next, { replace: true });
  }, [live.ticker, parsed.family, parsed.tab, resolved.expiry, resolved.dte, resolved.strategy, asOfDate, setParams]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__WORKSPACE_LIVE_AUDIT = {
      symbol,
      tab: workspaceTab,
      requestLog: live.requestLog,
      resources: Object.fromEntries(
        Object.entries(live.resources || {}).map(([k, v]) => [k, { status: v.status, error: v.error, fetchedAt: v.fetchedAt }]),
      ),
      mapping: live.ticker?.mappingReport || null,
      writes: (live.requestLog || []).filter((e) => e.method !== "GET").length,
      externalProviderCalls: (live.requestLog || []).filter((e) => e.externalProvider).length,
    };
    return undefined;
  }, [symbol, workspaceTab, live.requestLog, live.resources, live.ticker]);

  const changeTab = (nextWorkspaceTab) => {
    const next = buildHandoffSearch({
      expiry: resolved.expiry,
      dte: resolved.dte,
      family: resolved.family,
      strategy: resolved.strategy,
      tab: toIntelligenceTab(nextWorkspaceTab),
    }, { includeTab: true, asOfDate, expirations: live.ticker?.availableExpirations });
    setParams(next, { replace: true });
  };

  const toolsHref = strategyToolsPath(symbol, resolved, {
    asOfDate,
    expirations: live.ticker?.availableExpirations,
  });
  const handoffCopy = formatResearchHandoffCopy(
    live.ticker?.researchFocus
    || (parsed.strategy ? { kind: "pending" } : { kind: "choose" }),
  );

  return (
    <WorkspaceAdminProvider value={isAdmin}>
      <div
        className="p-3 md:p-6 space-y-3 md:space-y-4"
        style={{ color: "var(--hoip-text-primary)" }}
        data-testid="ticker-intelligence"
      >
        <div
          className="border p-2.5 flex flex-wrap items-center gap-x-4 gap-y-2"
          style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
        >
          <span
            className="text-[11px] font-bold uppercase tracking-wide"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="intelligence-title"
          >
            Ticker Intelligence Workspace
          </span>
          <Link
            to={toolsHref}
            className="text-[11px] underline"
            style={{ color: "var(--hoip-text-secondary)" }}
            data-testid="open-strategy-tools"
          >
            Open Strategy Tools
          </Link>
          <CachedBadge text="Cached data" />
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono-data" data-testid="handoff-context">
            <span data-testid="handoff-symbol">{symbol}</span>
            <span data-testid="handoff-expiry">{resolved.expiry || "expiry not in query"}</span>
            <span data-testid="handoff-dte">{resolved.dte != null ? `${resolved.dte}d` : "dte not in query"}</span>
            <span data-testid="handoff-family">{handoffCopy.primary}</span>
            {handoffCopy.secondary ? (
              <span data-testid="handoff-strategy">{handoffCopy.secondary}</span>
            ) : null}
          </span>
        </div>

        {live.loadingHeader ? (
          <WorkspacePanel title="Loading cached data" testid="live-header-loading">
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>
              Loading cached ticker data for {symbol}…
            </p>
          </WorkspacePanel>
        ) : live.headerError ? (
          <WorkspacePanel title="Cached data unavailable" testid="live-header-error">
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>
              {typeof live.headerError === "string" ? live.headerError : "data unavailable"}
            </p>
          </WorkspacePanel>
        ) : live.ticker ? (
          <TickerWorkspace
            ticker={live.ticker}
            tab={workspaceTab}
            onTabChange={changeTab}
            initialExpiry={resolved.expiry}
            initialStrategy={resolved.strategy}
            handoff={resolved}
          />
        ) : null}

        {isAdmin ? (
          <>
            <WorkspaceLiveRequestAudit log={live.requestLog} resources={live.resources} />
            {live.ticker ? <WorkspaceLiveProvenance rows={live.ticker.provenanceRows} /> : null}
          </>
        ) : (
          <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="intelligence-user-notice">
            Data may be delayed. Timestamp unavailable fields stay labelled as such. Indicative estimate only where pricing method is modelled.
          </p>
        )}
      </div>
    </WorkspaceAdminProvider>
  );
}
